import axios, {
  AxiosInstance,
  AxiosError,
  AxiosRequestConfig,
  AxiosResponse,
  InternalAxiosRequestConfig,
} from "axios";

import { getConfig } from "./apiConfig";
import { APIResponse } from "@api/@types";

export interface HttpClientConfig extends Omit<AxiosRequestConfig, "baseURL"> {
  retryCount?: number;
  retryDelay?: number;
  timeout?: number;
}

export interface InterceptorManager {
  request?: {
    onFulfilled?: (
      config: InternalAxiosRequestConfig,
    ) => InternalAxiosRequestConfig | Promise<InternalAxiosRequestConfig>;
    onRejected?: (error: any) => any;
  };
  response?: {
    onFulfilled?: (
      response: AxiosResponse,
    ) => AxiosResponse | Promise<AxiosResponse>;
    onRejected?: (error: AxiosError) => any;
  };
}

export interface HttpClientConfig extends Omit<AxiosRequestConfig, "baseURL"> {
  retryCount?: number;
  retryDelay?: number;
  timeout?: number;
  interceptors?: InterceptorManager;
}

export class HttpClient {
  private readonly instance: AxiosInstance;
  private readonly config: Required<
    Pick<HttpClientConfig, "retryCount" | "retryDelay" | "timeout">
  > &
    HttpClientConfig;
  private isRefreshing = false;
  private refreshSubscribers: ((token: string) => void)[] = [];
  private interceptorIds = {
    request: [] as number[],
    response: [] as number[],
  };

  private static readonly DEFAULT_CONFIG = {
    retryCount: 1,
    retryDelay: 1000,
    timeout: 30000,
  } as const;

  public getAxiosInstance(): AxiosInstance {
    return this.instance;
  }

  constructor(baseURL: string, config: HttpClientConfig = {}) {
    this.config = {
      ...HttpClient.DEFAULT_CONFIG,
      ...config,
    };
    this.instance = axios.create({
      baseURL,
      timeout: this.config.timeout,
      headers: {
        "Content-Type": "application/json",
      },
      ...config,
    });

    // 생성자에서 전달받은 인터셉터 설정
    if (config.interceptors) {
      this.setInterceptors(config.interceptors);
    }
  }

  public addRequestInterceptor(
    onFulfilled?: (
      config: InternalAxiosRequestConfig,
    ) => InternalAxiosRequestConfig | Promise<InternalAxiosRequestConfig>,
    onRejected?: (error: any) => any,
  ): number {
    const id = this.instance.interceptors.request.use(onFulfilled, onRejected);
    this.interceptorIds.request.push(id);
    return id;
  }

  public addResponseInterceptor(
    onFulfilled?: (
      response: AxiosResponse,
    ) => AxiosResponse | Promise<AxiosResponse>,
    onRejected?: (error: any) => any,
  ): number {
    const id = this.instance.interceptors.response.use(onFulfilled, onRejected);
    this.interceptorIds.response.push(id);
    return id;
  }

  public removeRequestInterceptor(id: number): void {
    this.instance.interceptors.request.eject(id);
    this.interceptorIds.request = this.interceptorIds.request.filter(
      (interceptorId) => interceptorId !== id,
    );
  }

  public removeResponseInterceptor(id: number): void {
    this.instance.interceptors.response.eject(id);
    this.interceptorIds.response = this.interceptorIds.response.filter(
      (interceptorId) => interceptorId !== id,
    );
  }

  public setInterceptors(interceptors: InterceptorManager): void {
    if (interceptors.request) {
      this.addRequestInterceptor(
        interceptors.request.onFulfilled,
        interceptors.request.onRejected,
      );
    }
    if (interceptors.response) {
      this.addResponseInterceptor(
        interceptors.response.onFulfilled,
        interceptors.response.onRejected,
      );
    }
  }

  private createErrorResponse<T>(
    error: AxiosError<APIResponse<T>>,
  ): APIResponse<T> {
    if (error.response?.data) {
      return error.response.data;
    }

    return {
      success: false,
      code: String(error.response?.status || "UNKNOWN"),
      message: error.message || "서버 에러가 발생했습니다.",
      data: null as T,
    };
  }

  public async request<T>(config: AxiosRequestConfig): Promise<APIResponse<T>> {
    try {
      const response = await this.instance.request<APIResponse<T>>(config);
      console.log(`[API RES] : ${response}`);
      return response.data;
    } catch (error) {
      if (error instanceof AxiosError) {
        return this.createErrorResponse(error);
      }
      return {
        success: false,
        code: "UNKNOWN",
        message: "알 수 없는 에러가 발생했습니다.",
        data: null as T,
      };
    }
  }

  public async get<T>(
    url: string,
    config?: AxiosRequestConfig,
  ): Promise<APIResponse<T>> {
    return this.request({ ...config, method: "GET", url });
  }

  public async post<T>(
    url: string,
    data?: unknown,
    config?: AxiosRequestConfig,
  ): Promise<APIResponse<T>> {
    return this.request({ ...config, method: "POST", url, data });
  }

  public async put<T>(
    url: string,
    data?: unknown,
    config?: AxiosRequestConfig,
  ): Promise<APIResponse<T>> {
    return this.request({ ...config, method: "PUT", url, data });
  }

  public async patch<T>(
    url: string,
    data?: unknown,
    config?: AxiosRequestConfig,
  ): Promise<APIResponse<T>> {
    return this.request({ ...config, method: "PATCH", url, data });
  }

  public async delete<T>(
    url: string,
    config?: AxiosRequestConfig,
  ): Promise<APIResponse<T>> {
    return this.request({ ...config, method: "DELETE", url });
  }
}

export const createApiClient = (baseUrl: string, config?: HttpClientConfig) => {
  return new HttpClient(baseUrl, config);
};

// 싱글톤 인스턴스
let apiClientInstance: HttpClient | null = null;

// 프로젝트별 인터셉터 설정을 위한 전역 변수
let getProjectInterceptors: (() => InterceptorManager | undefined) | undefined;

// 프로젝트별 인터셉터 설정 함수
export const setProjectInterceptors = (
  interceptorGetter: () => InterceptorManager | undefined,
): void => {
  getProjectInterceptors = interceptorGetter;
  // 기존 인스턴스 초기화하여 새 인터셉터 적용
  apiClientInstance = null;
};

// API 클라이언트 가져오기 (지연 초기화)
export const getApiClient = (): HttpClient => {
  if (!apiClientInstance) {
    const config = getConfig();
    // 프로젝트별 인터셉터 설정 가져오기
    const projectInterceptors = getProjectInterceptors?.();

    apiClientInstance = createApiClient(config.baseUrl, {
      timeout: config.timeout,
      retryCount: config.retryCount,
      retryDelay: config.retryDelay,
      interceptors: projectInterceptors,
    });
  }
  return apiClientInstance;
};

// 사용하기 쉽도록 래핑
export const apiClient = {
  get: <T>(url: string, config?: AxiosRequestConfig) =>
    getApiClient().get<T>(url, config),
  post: <T>(url: string, data?: unknown, config?: AxiosRequestConfig) =>
    getApiClient().post<T>(url, data, config),
  put: <T>(url: string, data?: unknown, config?: AxiosRequestConfig) =>
    getApiClient().put<T>(url, data, config),
  patch: <T>(url: string, data?: unknown, config?: AxiosRequestConfig) =>
    getApiClient().patch<T>(url, data, config),
  delete: <T>(url: string, config?: AxiosRequestConfig) =>
    getApiClient().delete<T>(url, config),
};
